import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import {
  Search, Image, FolderOpen, Upload, CheckCircle, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AssetPicker - Modal component for selecting assets from the library
 * Used in Design Editor to insert images into canvas
 */
const AssetPicker = ({ 
  open, 
  onClose, 
  onSelect, 
  filterType = 'image',
  multiple = false,
  title = 'Select an Asset'
}) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      let url = `${API}/assets?limit=50`;
      if (filterType && filterType !== 'all') url += `&asset_type=${filterType}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAssets(response.data?.assets || []);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, searchQuery]);

  useEffect(() => {
    if (open) {
      fetchAssets();
      setSelectedIds([]);
    }
  }, [open, fetchAssets]);

  const handleSelect = (asset) => {
    if (multiple) {
      setSelectedIds(prev => 
        prev.includes(asset.id) 
          ? prev.filter(id => id !== asset.id)
          : [...prev, asset.id]
      );
    } else {
      // Single select - immediately confirm
      const assetUrl = `${BACKEND_URL}${asset.url}`;
      onSelect({ 
        ...asset, 
        fullUrl: assetUrl 
      });
      onClose();
    }
  };

  const handleConfirmMultiple = () => {
    const selected = assets.filter(a => selectedIds.includes(a.id));
    const withUrls = selected.map(a => ({
      ...a,
      fullUrl: `${BACKEND_URL}${a.url}`
    }));
    onSelect(withUrls);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="dark:bg-slate-800 max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Image className="h-5 w-5 text-blue-500" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Select {filterType === 'image' ? 'an image' : 'a file'} from your asset library
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets..."
            className="pl-9 dark:bg-slate-700"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="asset-picker-search"
          />
        </div>

        {/* Asset Grid */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No {filterType === 'image' ? 'images' : 'assets'} found</p>
              <p className="text-sm mt-1">Upload some files to your asset library first</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 pb-4" data-testid="asset-picker-grid">
              {assets.map(asset => {
                const isSelected = selectedIds.includes(asset.id);
                
                return (
                  <div
                    key={asset.id}
                    className={`
                      relative aspect-square rounded-lg overflow-hidden cursor-pointer 
                      border-2 transition-all hover:border-blue-400
                      ${isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-transparent'}
                    `}
                    onClick={() => handleSelect(asset)}
                    data-testid={`asset-picker-item-${asset.id}`}
                  >
                    {asset.asset_type === 'image' ? (
                      <img 
                        src={`${BACKEND_URL}${asset.url}`}
                        alt={asset.name}
                        className="w-full h-full object-cover bg-slate-100 dark:bg-slate-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-700">
                        <FolderOpen className="h-8 w-8 text-slate-400" />
                      </div>
                    )}
                    
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    )}
                    
                    {/* Name overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-xs text-white truncate">{asset.name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {multiple && selectedIds.length > 0 && (
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleConfirmMultiple}>
              Insert {selectedIds.length} Image{selectedIds.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetPicker;
